import { CommentData, LaneData, NodeData, SpecMapState } from './types';

const doneStatuses = new Set(['Approved', 'Complete']);

export interface ProgressReportRow {
  id: string;
  title: string;
  lane: string;
  category: string;
  status: string;
  priority: string;
  owner: string;
  screens: number;
  dependencies: number;
  comments: number;
  updatedAt: string;
}

export interface ProgressReportPayload {
  generatedAt: string;
  specMap: {
    id: string;
    title: string;
    status: string;
    ownerName: string;
    clientName?: string;
  };
  totals: {
    nodes: number;
    screens: number;
    dependencies: number;
    comments: number;
    completion: number;
    highPriorityReview: number;
  };
  laneMaturity: Array<{
    id: string;
    label: string;
    description: string;
    nodes: number;
    approved: number;
    completion: number;
  }>;
  statusDistribution: Array<{ label: string; count: number }>;
  priorityDistribution: Array<{ label: string; count: number }>;
  ownerWorkload: Array<{
    owner: string;
    nodes: number;
    screens: number;
    highPriority: number;
    inReview: number;
  }>;
  highPriorityReview: ProgressReportRow[];
  dependencyRisks: Array<{
    sourceId: string;
    sourceTitle: string;
    dependencyId: string;
    label: string;
    status: string;
    targetNodeId?: string | null;
  }>;
  openComments: Array<CommentData & { nodeTitle: string }>;
  rows: ProgressReportRow[];
}

function activeNodes(state: SpecMapState) {
  return state.nodes.filter((node) => !node.archivedAt);
}

function countBy<T>(items: T[], getKey: (item: T) => string) {
  const counts = new Map<string, number>();

  items.forEach((item) => {
    const key = getKey(item) || 'Unassigned';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function completion(nodes: NodeData[]) {
  if (nodes.length === 0) {
    return 0;
  }

  return Math.round((nodes.filter((node) => doneStatuses.has(node.status)).length / nodes.length) * 100);
}

function laneLabel(lanes: LaneData[], laneId: string) {
  return lanes.find((lane) => lane.id === laneId)?.label ?? laneId;
}

export function buildProgressReport(state: SpecMapState, generatedAt = new Date().toISOString()): ProgressReportPayload {
  const nodes = activeNodes(state);
  const commentsByNode = new Map<string, CommentData[]>();

  state.comments.forEach((comment) => {
    commentsByNode.set(comment.nodeId, [...(commentsByNode.get(comment.nodeId) ?? []), comment]);
  });

  const rows: ProgressReportRow[] = nodes.map((node) => ({
    id: node.id,
    title: node.title,
    lane: laneLabel(state.lanes, node.lane),
    category: node.category,
    status: node.status,
    priority: node.priority,
    owner: node.owner,
    screens: node.childScreens.length,
    dependencies: node.dependencies.length,
    comments: commentsByNode.get(node.id)?.length ?? 0,
    updatedAt: node.updatedAt,
  }));

  const ownerWorkload = Array.from(
    nodes.reduce((owners, node) => {
      const key = node.owner || 'Unassigned';
      const current = owners.get(key) ?? {
        owner: key,
        nodes: 0,
        screens: 0,
        highPriority: 0,
        inReview: 0,
      };

      current.nodes += 1;
      current.screens += node.childScreens.length;
      current.highPriority += node.priority === 'High' ? 1 : 0;
      current.inReview += node.status === 'In Review' ? 1 : 0;
      owners.set(key, current);
      return owners;
    }, new Map<string, { owner: string; nodes: number; screens: number; highPriority: number; inReview: number }>())
  )
    .map(([, value]) => value)
    .sort((a, b) => b.nodes - a.nodes || a.owner.localeCompare(b.owner));

  const dependencyRisks = nodes.flatMap((node) =>
    node.dependencies
      .filter((dependency) => ['Draft', 'In Review', 'Deprecated'].includes(dependency.status))
      .map((dependency) => ({
        sourceId: node.id,
        sourceTitle: node.title,
        dependencyId: dependency.id,
        label: dependency.label,
        status: dependency.status,
        targetNodeId: dependency.targetNodeId,
      }))
  );

  const openComments = state.comments
    .map((comment) => ({
      ...comment,
      nodeTitle: nodes.find((node) => node.id === comment.nodeId)?.title ?? 'Unknown node',
    }))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return {
    generatedAt,
    specMap: {
      id: state.specMap.id,
      title: state.specMap.title,
      status: state.specMap.status,
      ownerName: state.specMap.ownerName,
      clientName: state.specMap.clientName,
    },
    totals: {
      nodes: nodes.length,
      screens: nodes.reduce((total, node) => total + node.childScreens.length, 0),
      dependencies: nodes.reduce((total, node) => total + node.dependencies.length, 0),
      comments: state.comments.length,
      completion: completion(nodes),
      highPriorityReview: nodes.filter(
        (node) => node.priority === 'High' && ['Draft', 'In Review'].includes(node.status)
      ).length,
    },
    laneMaturity: state.lanes.map((lane) => {
      const laneNodes = nodes.filter((node) => node.lane === lane.id);
      return {
        id: lane.id,
        label: lane.label,
        description: lane.description,
        nodes: laneNodes.length,
        approved: laneNodes.filter((node) => doneStatuses.has(node.status)).length,
        completion: completion(laneNodes),
      };
    }),
    statusDistribution: countBy(nodes, (node) => node.status),
    priorityDistribution: countBy(nodes, (node) => node.priority),
    ownerWorkload,
    highPriorityReview: rows.filter((row) => row.priority === 'High' && ['Draft', 'In Review'].includes(row.status)),
    dependencyRisks,
    openComments,
    rows,
  };
}

function csvEscape(value: string | number) {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function progressReportToCsv(report: ProgressReportPayload) {
  const headers = [
    'ID',
    'Title',
    'Lane',
    'Category',
    'Status',
    'Priority',
    'Owner',
    'Screens',
    'Dependencies',
    'Comments',
    'Updated At',
  ];

  const rows = report.rows.map((row) => [
    row.id,
    row.title,
    row.lane,
    row.category,
    row.status,
    row.priority,
    row.owner,
    row.screens,
    row.dependencies,
    row.comments,
    row.updatedAt,
  ]);

  return [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
}
